import { DatePicker } from '@atlaskit/datetime-picker';
import { Label } from '@atlaskit/form';
import SearchIcon from '@atlaskit/icon/glyph/search';
import Select from '@atlaskit/select';
import TextField from '@atlaskit/textfield';
import React, { ChangeEvent, Dispatch, SetStateAction } from 'react';
import styled from 'styled-components';

import { TogglWorkspace } from '../../../../types/toggl-api';
import {
  getDateMinusDays,
  getDatePlusDays,
  getOneYearAgo,
  getToday,
} from '../utils';

export interface Filters {
  workspaceIds: number[];
  search: string;
  startDate: string;
  endDate: string;
}

interface SearchFiltersProps {
  filters: Filters;
  setFilters: Dispatch<SetStateAction<Filters>>;
  workspaces: TogglWorkspace[];
}

export function SearchFilters({
  filters,
  setFilters,
  workspaces,
}: SearchFiltersProps) {
  const workspaceOptions = workspaces.map((workspace) => ({
    label: workspace.name,
    value: workspace.id,
  }));

  const updateStartDateFilter = (update: string) => {
    setFilters((prev) => ({
      ...prev,
      startDate: update || getOneYearAgo(),
    }));
  };

  const updateEndDateFilter = (update: string) => {
    setFilters((prev) => ({
      ...prev,
      endDate: update || getToday(),
    }));
  };

  const updateTextFilter = (e: ChangeEvent<HTMLInputElement>) => {
    setFilters((prev) => ({ ...prev, search: e.target.value }));
  };

  return (
    <>
      <FirstLine>
        <DropDownWrapper>
          <Label htmlFor="workspace-multi-select">Toggl workspaces</Label>
          <Select
            inputId="workspace-multi-select"
            options={workspaceOptions}
            onChange={(update) =>
              setFilters((prev) => ({
                ...prev,
                workspaceIds: update.map((option) => option.value),
              }))
            }
            isMulti
            isSearchable={true}
            placeholder="Select workspaces to narrow results"
          />
        </DropDownWrapper>
        <DatePickerWrapper>
          <Label htmlFor="start-date-select">Start date</Label>
          <DatePicker
            id="start-date-select"
            hideIcon={true}
            minDate={getDateMinusDays(filters.endDate, 364)}
            maxDate={getDateMinusDays(filters.endDate, 1)}
            value={filters.startDate}
            onChange={updateStartDateFilter}
          />
        </DatePickerWrapper>
        <DatePickerWrapper>
          <Label htmlFor="end-date-select">End date</Label>
          <DatePicker
            id="end-date-select"
            hideIcon={true}
            value={filters.endDate}
            maxDate={getDatePlusDays(filters.startDate, 364)}
            minDate={getDatePlusDays(filters.startDate, 1)}
            onChange={updateEndDateFilter}
          />
        </DatePickerWrapper>
      </FirstLine>
      <TextFieldWrapper>
        <TextSearchField
          value={filters.search}
          onChange={updateTextFilter}
          elemBeforeInput={
            <span style={{ paddingLeft: 8 }}>
              <SearchIcon size="small" label="search-icon" />
            </span>
          }
        />
      </TextFieldWrapper>
    </>
  );
}

const FirstLine = styled.div`
  display: flex;
  gap: 16px;
`;

const DropDownWrapper = styled.div`
  flex: 1;
`;

const DatePickerWrapper = styled.div`
  flex: 1;
  & > div {
    height: 40px;
    align-items: center;
  }
`;

const TextFieldWrapper = styled.div`
  width: 240px;
`;

const TextSearchField = styled(TextField)`
  height: 32px;
  padding 8px;
`;
